import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { applyDocumentColorVision } from "../utils/colorVisionFilters";
import {
  applyDocumentColorScheme,
  getSystemPrefersDark,
  persistColorVision,
  persistThemePreference,
  readStoredColorVision,
  readStoredThemePreference,
  resolveMode,
} from "../theme/themePreference";

const UiPreferencesContext = createContext(null);

function subscribeToColorSchemeChanges(onChange) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleChange = (event) => {
    onChange(event.matches);
  };

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }

  mediaQuery.addListener(handleChange);
  return () => mediaQuery.removeListener(handleChange);
}

export function UiPreferencesProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState(
    readStoredThemePreference,
  );
  const [resolvedMode, setResolvedMode] = useState(() =>
    resolveMode(readStoredThemePreference(), getSystemPrefersDark()),
  );
  const [colorVision, setColorVisionState] = useState(readStoredColorVision);

  useLayoutEffect(() => {
    setResolvedMode(resolveMode(themePreference, getSystemPrefersDark()));

    if (themePreference !== "system") {
      return undefined;
    }

    return subscribeToColorSchemeChanges((systemDark) => {
      setResolvedMode(resolveMode("system", systemDark));
    });
  }, [themePreference]);

  useLayoutEffect(() => {
    applyDocumentColorScheme(resolvedMode);
  }, [resolvedMode]);

  useLayoutEffect(() => {
    applyDocumentColorVision(colorVision);
  }, [colorVision]);

  const setThemePreference = useCallback((nextPreference) => {
    const persisted = persistThemePreference(nextPreference);
    setThemePreferenceState(persisted);
  }, []);

  const setColorVision = useCallback((nextMode) => {
    const persisted = persistColorVision(nextMode);
    setColorVisionState(persisted);
  }, []);

  const value = useMemo(
    () => ({
      themePreference,
      resolvedMode,
      setThemePreference,
      colorVision,
      setColorVision,
    }),
    [
      themePreference,
      resolvedMode,
      setThemePreference,
      colorVision,
      setColorVision,
    ],
  );

  return (
    <UiPreferencesContext.Provider value={value}>
      {children}
    </UiPreferencesContext.Provider>
  );
}

export function useUiPreferences() {
  const context = useContext(UiPreferencesContext);
  if (!context) {
    throw new Error(
      "useUiPreferences deve ser usado dentro de UiPreferencesProvider",
    );
  }
  return context;
}
